import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, X, AlertCircle, CheckCircle, User, DollarSign, Users, Activity,
  TrendingUp, Clock, Calendar, CreditCard, Settings, Search,
  Edit, Save, Phone, Mail, Wallet, Eye, MessageSquare, Gift, Plus, Trash2
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import BlogManager from './BlogManager';
import ActiveUsersTracker from './ActiveUsersTracker';
import CreditsManager from './CreditsManager';

// Interfaces based on your exact database structure
interface AdvisorApplication {
  id: string;
  display_name: string;
  specialty: string;
  bio: string;
  created_at: string;
  approved: boolean;
  user_email?: string;
}

interface DashboardStats {
  totalRevenue: number;
  monthlyRevenue: number;
  totalClients: number;
  totalAdvisors: number;
  activeAdvisors: number;
  averageRating: number;
  totalReadings: number;
  platformFees: number;
  totalWalletFunds: number;
  advisorEarnings: number;
}

interface ClientWallet {
  id: string;
  user_id: string;
  balance: number;
  user_name: string;
  user_email: string;
  created_at: string;
  updated_at: string;
}

interface AdvisorEarning {
  id: string;
  advisor_id: string;
  advisor_name: string;
  advisor_email: string;
  phone_number: string;
  total_earned: number;
  available_balance: number;
  pending_balance: number;
  commission_rate: number;
  platform_fee_percentage: number;
  status: string;
}

interface MonthlyPayout {
  advisor_id: string;
  advisor_name: string;
  advisor_email: string;
  phone_number: string;
  monthly_earnings: number;
  transaction_count: number;
  platform_fees: number;
  amount_to_pay: number;
  last_payout_date: string | null;
}

export default function EnhancedAdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State management
  const [applications, setApplications] = useState<AdvisorApplication[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalClients: 0,
    totalAdvisors: 0,
    activeAdvisors: 0,
    averageRating: 0,
    totalReadings: 0,
    platformFees: 0,
    totalWalletFunds: 0,
    advisorEarnings: 0
  });
  
  const [clientWallets, setClientWallets] = useState<ClientWallet[]>([]);
  const [advisorEarnings, setAdvisorEarnings] = useState<AdvisorEarning[]>([]);
  const [monthlyPayouts, setMonthlyPayouts] = useState<MonthlyPayout[]>([]);
  const [payoutDays, setPayoutDays] = useState(30);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'advisors' | 'wallets' | 'earnings' | 'payouts' | 'conversations' | 'clients' | 'voicecalls' | 'blog' | 'credits'>('overview');
  const [generatingBlog, setGeneratingBlog] = useState(false);
  const [lastGenerationAttempt, setLastGenerationAttempt] = useState<Date | null>(null);
  
  // Editing states
  const [editingWallet, setEditingWallet] = useState<string | null>(null);
  const [editingCommission, setEditingCommission] = useState<string | null>(null);
  const [editingPhone, setEditingPhone] = useState<string | null>(null);
  const [tempValues, setTempValues] = useState<{[key: string]: string}>({});
  
  // Search states
  const [clientSearch, setClientSearch] = useState('');
  const [advisorSearch, setAdvisorSearch] = useState('');
  const [conversationSearch, setConversationSearch] = useState('');

  // Conversation and client management state
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const [conversationMessages, setConversationMessages] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [addingCreditsTo, setAddingCreditsTo] = useState<string | null>(null);
  const [creditAmount, setCreditAmount] = useState<{ [key: string]: string }>({});

  // Voice calls state
  const [voiceCalls, setVoiceCalls] = useState<any[]>([]);
  const [voiceCallSearch, setVoiceCallSearch] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkAdminAccess();
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      fetchAllData();
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'conversations' && user) {
      fetchConversations();
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab === 'clients' && user) {
      fetchClients();
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab === 'voicecalls' && user) {
      fetchVoiceCalls();
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab === 'payouts' && user) {
      fetchMonthlyPayouts();
    }
  }, [activeTab, payoutDays, user]);

  const checkAdminAccess = async () => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('admin_user')
        .eq('id', user?.id)
        .single();

      if (profileError || !profile?.admin_user) {
        navigate('/');
        return;
      }
    } catch (err) {
      console.error('Error checking admin access:', err);
      navigate('/');
    }
  };

  const fetchAllData = async () => {
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchApplications(),
        fetchClientWallets(),
        fetchAdvisorEarnings(),
        fetchMonthlyPayouts()
      ]);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      console.log('Fetching dashboard stats via Edge Function...');
      
      // Get the current session for authorization
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('Failed to get session: ' + sessionError.message);
      }

      if (!session) {
        throw new Error('No active session found');
      }

      // Call the Edge Function to get dashboard stats (bypasses RLS)
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-admin-dashboard-stats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch dashboard stats');
      }

      const statsData = await response.json();
      console.log('Received dashboard stats:', statsData);
      
      setStats(statsData);

    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard statistics: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const fetchApplications = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('Failed to get session: ' + sessionError.message);
      }

      if (!session) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-advisor-applications`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch applications');
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from server');
      }

      setApplications(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(
        err instanceof Error 
          ? `Failed to load advisor applications: ${err.message}` 
          : 'Failed to load advisor applications. Please try again.'
      );
      setApplications([]);
    }
  };

  const fetchClientWallets = async () => {
    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select(`
          id,
          user_id,
          balance,
          created_at,
          updated_at,
          profiles!user_wallets_user_id_fkey (
            display_name,
            email
          )
        `)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const formattedWallets = data?.map(wallet => ({
        id: wallet.id,
        user_id: wallet.user_id,
        balance: wallet.balance || 0,
        user_name: wallet.profiles?.display_name || 'Unknown',
        user_email: wallet.profiles?.email || 'Unknown',
        created_at: wallet.created_at,
        updated_at: wallet.updated_at
      })) || [];

      setClientWallets(formattedWallets);
    } catch (err) {
      console.error('Error fetching client wallets:', err);
    }
  };

  const fetchAdvisorEarnings = async () => {
    try {
      const { data: walletData, error: walletError } = await supabase
        .from('advisor_wallets')
        .select('*')
        .order('total_earned', { ascending: false });

      if (walletError) throw walletError;

      if (!walletData || walletData.length === 0) {
        setAdvisorEarnings([]);
        return;
      }

      const advisorIds = walletData.map(wallet => wallet.advisor_id);

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, display_name, email, phone_number, status, platform_fee_percentage')
        .in('id', advisorIds);

      if (profileError) throw profileError;

      const formattedEarnings = walletData.map(wallet => {
        const profile = profileData?.find(p => p.id === wallet.advisor_id);
        
        return {
          id: wallet.id,
          advisor_id: wallet.advisor_id,
          advisor_name: profile?.display_name || 'Unknown',
          advisor_email: profile?.email || 'Unknown',
          phone_number: profile?.phone_number || '',
          total_earned: wallet.total_earned || 0,
          available_balance: wallet.available_balance || 0,
          pending_balance: wallet.pending_balance || 0,
          platform_fee_percentage: profile?.platform_fee_percentage || 20,
          commission_rate: 100 - (profile?.platform_fee_percentage || 20),
          status: profile?.status || 'offline'
        };
      });

      setAdvisorEarnings(formattedEarnings);
    } catch (err) {
      console.error('Error fetching advisor earnings:', err);
      setError('Failed to fetch advisor earnings: ' + err.message);
    }
  };

  const fetchConversations = async () => {
    try {
      const { data: conversationData, error: convError } = await supabase
        .from('conversations')
        .select('*')
        .order('created_at', { ascending: false });

      if (convError) throw convError;

      if (!conversationData || conversationData.length === 0) {
        setConversations([]);
        return;
      }

      // Get all unique client and advisor IDs
      const clientIds = [...new Set(conversationData.map(c => c.client_id).filter(Boolean))];
      const advisorIds = [...new Set(conversationData.map(c => c.advisor_id).filter(Boolean))];
      const allUserIds = [...new Set([...clientIds, ...advisorIds])];

      // Fetch all profiles at once
      const { data: profilesData, error: profileError } = await supabase
        .from('profiles')
        .select('id, display_name, email, user_type')
        .in('id', allUserIds);

      if (profileError) throw profileError;

      // Map conversations with profile data
      const conversationsWithProfiles = conversationData.map(conv => ({
        ...conv,
        client: profilesData?.find(p => p.id === conv.client_id),
        advisor: profilesData?.find(p => p.id === conv.advisor_id)
      }));

      setConversations(conversationsWithProfiles);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to fetch conversations: ' + (err as any).message);
    }
  };

  const fetchConversationMessages = async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:profiles!messages_sender_id_fkey(display_name, email, user_type)
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setConversationMessages(data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to fetch messages');
    }
  };

  const fetchVoiceCalls = async () => {
    try {
      // Fetch from active_calls table (the current system)
      const { data: callData, error: callError } = await supabase
        .from('active_calls')
        .select('*')
        .order('created_at', { ascending: false });

      if (callError) throw callError;

      if (!callData || callData.length === 0) {
        setVoiceCalls([]);
        return;
      }

      // Get all unique user and advisor IDs
      const userIds = [...new Set(callData.map(c => c.client_id).filter(Boolean))];
      const advisorIds = [...new Set(callData.map(c => c.advisor_id).filter(Boolean))];
      const allUserIds = [...new Set([...userIds, ...advisorIds])];

      // Fetch all profiles at once
      const { data: profilesData, error: profileError } = await supabase
        .from('profiles')
        .select('id, display_name, email, user_type')
        .in('id', allUserIds);

      if (profileError) throw profileError;

      // Map calls with profile data and convert to match expected format
      const callsWithProfiles = callData.map(call => ({
        id: call.id,
        user_id: call.client_id,
        advisor_id: call.advisor_id,
        call_sid: call.conference_sid || 'N/A',
        start_time: call.started_at || call.created_at,
        end_time: call.ended_at,
        duration: call.total_minutes ? call.total_minutes * 60 : null,
        status: call.status,
        cost: parseFloat(call.total_cost) || 0,
        rating: null,
        created_at: call.created_at,
        client: profilesData?.find(p => p.id === call.client_id),
        advisor: profilesData?.find(p => p.id === call.advisor_id)
      }));

      setVoiceCalls(callsWithProfiles);
    } catch (err) {
      console.error('Error fetching voice calls:', err);
      setError('Failed to fetch voice calls: ' + (err as any).message);
    }
  };

  const fetchClients = async () => {
    try {
      // Get all client profiles
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_type', 'client')
        .order('created_at', { ascending: false });

      if (profileError) throw profileError;

      // Get wallet info for each client
      const { data: walletData, error: walletError } = await supabase
        .from('user_wallets')
        .select('user_id, balance');

      if (walletError) throw walletError;

      // Get credits info for each client
      const { data: creditsData, error: creditsError } = await supabase
        .from('user_credits')
        .select('user_id, free_minutes_remaining');

      if (creditsError) throw creditsError;

      const { data: messageCreditsData, error: messageCreditsError } = await supabase
        .from('message_credits')
        .select('user_id, credits_remaining');

      if (messageCreditsError) throw messageCreditsError;

      // Combine all data
      const clientsWithDetails = profileData?.map(profile => {
        const wallet = walletData?.find(w => w.user_id === profile.id);
        const credits = creditsData?.find(c => c.user_id === profile.id);
        const messageCredits = messageCreditsData?.find(mc => mc.user_id === profile.id);

        return {
          ...profile,
          wallet_balance: wallet?.balance || 0,
          free_minutes: credits?.free_minutes_remaining || 0,
          message_credits: messageCredits?.credits_remaining || 0
        };
      }) || [];

      setClients(clientsWithDetails);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to fetch client data');
    }
  };

  const handleAddCredits = async (userId: string, minutes: number) => {
    try {
      const { error } = await supabase
        .from('user_credits')
        .update({
          free_minutes_remaining: minutes,
          free_minutes_used: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;

      setSuccess(`Added ${minutes} minutes to client's account`);
      setTimeout(() => setSuccess(null), 3000);

      setAddingCreditsTo(null);
      setCreditAmount({});
      fetchClients();
    } catch (err) {
      console.error('Error adding credits:', err);
      setError('Failed to add credits');
    }
  };

  const handleAddMessageCredits = async (userId: string, credits: number) => {
    try {
      const { error } = await supabase
        .from('message_credits')
        .update({
          credits_remaining: credits,
          free_messages_used: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;

      setSuccess(`Added ${credits} message credits to client's account`);
      setTimeout(() => setSuccess(null), 3000);

      setAddingCreditsTo(null);
      setCreditAmount({});
      fetchClients();
    } catch (err) {
      console.error('Error adding message credits:', err);
      setError('Failed to add message credits');
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (!confirm('Are you sure you want to delete this conversation? This will also delete all messages in this conversation.')) {
      return;
    }

    try {
      // Delete the conversation - CASCADE will automatically delete all messages
      const { error: conversationError } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId);

      if (conversationError) {
        console.error('Conversation deletion error:', conversationError);
        throw conversationError;
      }

      setSuccess('Conversation deleted successfully');
      setTimeout(() => setSuccess(null), 3000);

      // Clear selected conversation if it was deleted
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setConversationMessages([]);
      }

      // Refresh the conversations list
      fetchConversations();
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation: ' + (err as any).message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const fetchMonthlyPayouts = async () => {
    try {
      console.log('Fetching monthly payouts for last', payoutDays, 'days');
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - payoutDays);

      const { data: transactions, error: transactionError } = await supabase
        .from('transactions')
        .select('advisor_id, advisor_earnings, platform_fee, created_at')
        .eq('status', 'completed')
        .gte('created_at', daysAgo.toISOString());

      if (transactionError) {
        console.error('Transaction error:', transactionError);
        throw transactionError;
      }

      console.log('Found transactions:', transactions?.length || 0);

      if (!transactions || transactions.length === 0) {
        console.log('No transactions found for the period');
        setMonthlyPayouts([]);
        return;
      }

      const { data: walletData, error: walletError } = await supabase
        .from('advisor_wallets')
        .select('advisor_id, last_payout_at');

      if (walletError) {
        console.error('Wallet error:', walletError);
      }

      const advisorIds = [...new Set(transactions.map(t => t.advisor_id))];
      console.log('Unique advisors:', advisorIds.length);

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, display_name, email, phone_number')
        .in('id', advisorIds);

      if (profileError) {
        console.error('Profile error:', profileError);
        throw profileError;
      }

      const payoutMap = new Map<string, MonthlyPayout>();

      transactions.forEach(transaction => {
        const advisorId = transaction.advisor_id;
        const profile = profiles?.find(p => p.id === advisorId);
        const wallet = walletData?.find(w => w.advisor_id === advisorId);

        if (!payoutMap.has(advisorId)) {
          payoutMap.set(advisorId, {
            advisor_id: advisorId,
            advisor_name: profile?.display_name || 'Unknown',
            advisor_email: profile?.email || 'Unknown',
            phone_number: profile?.phone_number || '',
            monthly_earnings: 0,
            transaction_count: 0,
            platform_fees: 0,
            amount_to_pay: 0,
            last_payout_date: wallet?.last_payout_at || null
          });
        }

        const payout = payoutMap.get(advisorId)!;
        payout.transaction_count++;
        const earnings = parseFloat(String(transaction.advisor_earnings)) || 0;
        const fees = parseFloat(String(transaction.platform_fee)) || 0;
        payout.monthly_earnings += earnings;
        payout.platform_fees += fees;
        payout.amount_to_pay = payout.monthly_earnings;
      });

      const payoutArray = Array.from(payoutMap.values())
        .sort((a, b) => b.amount_to_pay - a.amount_to_pay);

      console.log('Monthly payouts calculated:', payoutArray);
      setMonthlyPayouts(payoutArray);
    } catch (err) {
      console.error('Error fetching monthly payouts:', err);
      setError('Failed to fetch monthly payouts: ' + (err as any).message);
    }
  };

  const handleApproval = async (applicationId: string, approved: boolean) => {
    try {
      setError(null);
      setSuccess(null);
      
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('Failed to get session: ' + sessionError.message);
      }

      if (!session) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-advisor-approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          applicationId,
          approved
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update approval status');
      }

      // Update local state
      setApplications(apps => 
        apps.map(app => 
          app.id === applicationId ? { ...app, approved } : app
        )
      );

      setSuccess(`Application ${approved ? 'approved' : 'rejected'} successfully`);
      
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh dashboard stats after approval changes
      fetchDashboardStats();
    } catch (err) {
      console.error('Error updating approval status:', err);
      setError('Failed to update approval status');
    }
  };

  const handleWalletUpdate = async (walletId: string, newBalance: number) => {
    try {
      const { error } = await supabase
        .from('user_wallets')
        .update({ 
          balance: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', walletId);

      if (error) throw error;

      setClientWallets(wallets => 
        wallets.map(wallet => 
          wallet.id === walletId ? { ...wallet, balance: newBalance } : wallet
        )
      );

      setSuccess('Wallet balance updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      setEditingWallet(null);
      setTempValues({});
      
      await fetchDashboardStats();
      
    } catch (err) {
      console.error('Error updating wallet:', err);
      setError('Failed to update wallet balance');
    }
  };

  const handleCommissionUpdate = async (advisorId: string, newCommissionRate: number) => {
    try {
      const platformFeePercentage = 100 - newCommissionRate;

      const { error } = await supabase
        .from('profiles')
        .update({
          platform_fee_percentage: platformFeePercentage,
          updated_at: new Date().toISOString()
        })
        .eq('id', advisorId);

      if (error) throw error;

      setAdvisorEarnings(advisors => 
        advisors.map(advisor => 
          advisor.advisor_id === advisorId 
            ? { 
                ...advisor, 
                commission_rate: newCommissionRate,
                platform_fee_percentage: platformFeePercentage 
              } 
            : advisor
        )
      );

      setSuccess('Commission rate updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      setEditingCommission(null);
      setTempValues({});
      
    } catch (err) {
      console.error('Error updating commission:', err);
      setError('Failed to update commission rate');
    }
  };

  const handlePhoneUpdate = async (advisorId: string, newPhone: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          phone_number: newPhone,
          updated_at: new Date().toISOString()
        })
        .eq('id', advisorId);

      if (error) throw error;

      setAdvisorEarnings(advisors => 
        advisors.map(advisor => 
          advisor.advisor_id === advisorId 
            ? { ...advisor, phone_number: newPhone } 
            : advisor
        )
      );

      setSuccess('Phone number updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      setEditingPhone(null);
      setTempValues({});
      
    } catch (err) {
      console.error('Error updating phone:', err);
      setError('Failed to update phone number');
    }
  };

  const navigateToStripeWebhook = () => {
    navigate('/stripe-webhook');
  };

  const canGenerateBlogPost = () => {
    if (!lastGenerationAttempt) return true;
    const timeSinceLastAttempt = Date.now() - lastGenerationAttempt.getTime();
    return timeSinceLastAttempt >= 5 * 60 * 1000;
  };

  const generateDailyBlogPost = async () => {
    try {
      if (!canGenerateBlogPost()) {
        setError('Please wait 5 minutes between blog post generation attempts');
        return;
      }

      setError(null);
      setSuccess(null);
      setGeneratingBlog(true);
      setLastGenerationAttempt(new Date());
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/daily-blog-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          throw new Error('OpenAI API quota exceeded. Please try again later or contact support to upgrade your plan.');
        }
        throw new Error(errorData.error || 'Failed to generate blog post');
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Blog post generated successfully!');
      } else {
        throw new Error(data.message || 'Failed to generate blog post');
      }
    } catch (err) {
      console.error('Error generating blog post:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate blog post');
    } finally {
      setGeneratingBlog(false);
    }
  };

  // Filter functions
  const filteredClientWallets = clientWallets.filter(wallet =>
    wallet.user_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    wallet.user_email.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredAdvisorEarnings = advisorEarnings.filter(advisor =>
    advisor.advisor_name.toLowerCase().includes(advisorSearch.toLowerCase()) ||
    advisor.advisor_email.toLowerCase().includes(advisorSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-purple-900 text-white py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold">Enhanced Admin Dashboard</h1>
          <p className="mt-2 text-purple-200">Complete Platform Management & Control</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
            <AlertCircle className="w-5 h-5 mr-2" />
            <p>{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg flex items-center text-green-700">
            <CheckCircle className="w-5 h-5 mr-2" />
            <p>{success}</p>
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Admin Tools */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Admin Tools</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={navigateToStripeWebhook}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <Settings className="w-5 h-5 mr-2" />
              Stripe Webhook Configuration
            </button>
            
            <button
              onClick={generateDailyBlogPost}
              disabled={generatingBlog || !canGenerateBlogPost()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingBlog ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : !canGenerateBlogPost() ? (
                <>
                  <Clock className="w-5 h-5 mr-2" />
                  Please wait...
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5 mr-2" />
                  Generate Daily Blog Post
                </>
              )}
            </button>
          </div>
        </div>

        {/* Enhanced Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'advisors', label: 'Advisor Applications', icon: User },
              { id: 'wallets', label: 'Client Wallets', icon: Wallet },
              { id: 'earnings', label: 'Advisor Earnings', icon: TrendingUp },
              { id: 'payouts', label: 'Monthly Payouts', icon: DollarSign },
              { id: 'conversations', label: 'Conversations', icon: MessageSquare },
              { id: 'clients', label: 'Client Management', icon: Users },
              { id: 'voicecalls', label: 'Voice Calls', icon: Phone },
              { id: 'credits', label: 'Credits & Promos', icon: Gift },
              { id: 'blog', label: 'Blog Manager', icon: Edit }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Overview Tab - Enhanced */}
        {activeTab === 'overview' && (
          <>
            {/* Enhanced Dashboard Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Total Revenue</h3>
                  <DollarSign className="text-purple-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${stats.totalRevenue.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Platform Fees: ${stats.platformFees.toFixed(2)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Monthly Revenue</h3>
                  <TrendingUp className="text-green-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${stats.monthlyRevenue.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">Current Month</p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Total Readings</h3>
                  <Activity className="text-blue-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">{stats.totalReadings}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Avg Rating: {stats.averageRating.toFixed(1)}⭐
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Total Clients</h3>
                  <Users className="text-indigo-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">{stats.totalClients}</p>
                <p className="text-sm text-gray-500 mt-2">Active Users</p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Advisor Earnings</h3>
                  <CreditCard className="text-green-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${stats.advisorEarnings.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Total Advisors: {stats.totalAdvisors}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Wallet Funds</h3>
                  <Wallet className="text-purple-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">${stats.totalWalletFunds.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">Client Balances</p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Online Advisors</h3>
                  <CheckCircle className="text-green-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">{stats.activeAdvisors}</p>
                <p className="text-sm text-gray-500 mt-2">Currently Available</p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 text-sm">Platform Health</h3>
                  <Eye className="text-blue-600 w-6 h-6" />
                </div>
                <p className="text-2xl font-bold">
                  {stats.totalAdvisors > 0 ? ((stats.activeAdvisors / stats.totalAdvisors) * 100).toFixed(0) : 0}%
                </p>
                <p className="text-sm text-gray-500 mt-2">Advisor Activity</p>
              </div>
            </div>

            {/* Active Users Tracker */}
            <div className="mt-8">
              <ActiveUsersTracker />
            </div>
          </>
        )}

        {/* Advisor Applications Tab - Existing */}
        {activeTab === 'advisors' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Advisor Applications</h2>
            </div>

            {applications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {error ? 'Failed to load applications' : 'No pending applications'}
              </div>
            ) : (
              <div className="divide-y">
                {applications.map((application) => (
                  <div key={application.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <User className="w-5 h-5 text-gray-400 mr-2" />
                          <h3 className="text-lg font-semibold">{application.display_name}</h3>
                          <span className={`ml-3 px-2 py-1 text-xs font-medium rounded-full ${
                            application.approved
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {application.approved ? 'Approved' : 'Pending'}
                          </span>
                        </div>

                        <div className="mb-4">
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-medium">{application.user_email || 'Email not available'}</p>
                        </div>

                        <div className="mb-4">
                          <p className="text-sm text-gray-600">Specialty</p>
                          <p className="font-medium">{application.specialty}</p>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600">Bio</p>
                          <p className="text-gray-800">{application.bio}</p>
                        </div>
                      </div>

                      <div className="ml-6 flex space-x-2">
                        {!application.approved && (
                          <button
                            onClick={() => handleApproval(application.id, true)}
                            className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition-colors"
                            title="Approve"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleApproval(application.id, false)}
                          className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-colors"
                          title="Reject"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Client Wallets Tab - New with Inline Editing */}
        {activeTab === 'wallets' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Client Wallets</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredClientWallets.map((wallet) => (
                    <tr key={wallet.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-5 h-5 text-gray-400 mr-3" />
                          <span className="font-medium">{wallet.user_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {wallet.user_email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingWallet === wallet.id ? (
                          <div className="flex items-center space-x-2">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={tempValues[wallet.id] || wallet.balance.toString()}
                              onChange={(e) => setTempValues({...tempValues, [wallet.id]: e.target.value})}
                              className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <span className="font-medium text-green-600">
                            ${wallet.balance.toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-sm">
                        {new Date(wallet.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingWallet === wallet.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                const newBalance = parseFloat(tempValues[wallet.id] || wallet.balance.toString());
                                if (!isNaN(newBalance) && newBalance >= 0) {
                                  handleWalletUpdate(wallet.id, newBalance);
                                }
                              }}
                              className="bg-green-500 text-white p-1 rounded hover:bg-green-600 transition-colors"
                              title="Save"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingWallet(null);
                                setTempValues({});
                              }}
                              className="bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingWallet(wallet.id);
                              setTempValues({...tempValues, [wallet.id]: wallet.balance.toString()});
                            }}
                            className="bg-purple-500 text-white p-1 rounded hover:bg-purple-600 transition-colors"
                            title="Edit Balance"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Total Clients: {clientWallets.length} | 
                  Total Wallet Funds: ${clientWallets.reduce((sum, w) => sum + w.balance, 0).toFixed(2)}
                </span>
                <span className="text-xs text-gray-500">
                  Click the edit icon to modify wallet balances
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Advisor Earnings Tab - New with Inline Editing */}
        {activeTab === 'earnings' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Advisor Earnings & Management</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search advisors..."
                    value={advisorSearch}
                    onChange={(e) => setAdvisorSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Advisor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Earned</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAdvisorEarnings.map((advisor) => (
                    <tr key={advisor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-5 h-5 text-gray-400 mr-3" />
                          <div>
                            <div className="font-medium">{advisor.advisor_name}</div>
                            <div className="text-sm text-gray-500">{advisor.advisor_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingPhone === advisor.advisor_id ? (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <input
                              type="tel"
                              value={tempValues[advisor.advisor_id] || advisor.phone_number}
                              onChange={(e) => setTempValues({...tempValues, [advisor.advisor_id]: e.target.value})}
                              className="w-32 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <span className="text-gray-600">{advisor.phone_number || 'Not set'}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-green-600">
                        ${advisor.total_earned.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        ${advisor.available_balance.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        ${advisor.pending_balance.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingCommission === advisor.advisor_id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={tempValues[advisor.advisor_id] || advisor.commission_rate.toString()}
                              onChange={(e) => setTempValues({...tempValues, [advisor.advisor_id]: e.target.value})}
                              className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              autoFocus
                            />
                            <span className="text-sm text-gray-500">%</span>
                          </div>
                        ) : (
                          <span className="font-medium">{advisor.commission_rate.toFixed(1)}%</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-1">
                          {editingPhone === advisor.advisor_id ? (
                            <>
                              <button
                                onClick={() => {
                                  const newPhone = tempValues[advisor.advisor_id] || advisor.phone_number;
                                  handlePhoneUpdate(advisor.advisor_id, newPhone);
                                }}
                                className="bg-green-500 text-white p-1 rounded hover:bg-green-600 transition-colors"
                                title="Save Phone"
                              >
                                <Save className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingPhone(null);
                                  setTempValues({});
                                }}
                                className="bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingPhone(advisor.advisor_id);
                                setTempValues({...tempValues, [advisor.advisor_id]: advisor.phone_number});
                              }}
                              className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600 transition-colors"
                              title="Edit Phone"
                            >
                              <Phone className="w-3 h-3" />
                            </button>
                          )}

                          {editingCommission === advisor.advisor_id ? (
                            <>
                              <button
                                onClick={() => {
                                  const newRate = parseFloat(tempValues[advisor.advisor_id] || advisor.commission_rate.toString());
                                  if (!isNaN(newRate) && newRate >= 0 && newRate <= 100) {
                                    handleCommissionUpdate(advisor.advisor_id, newRate);
                                  }
                                }}
                                className="bg-green-500 text-white p-1 rounded hover:bg-green-600 transition-colors"
                                title="Save Commission"
                              >
                                <Save className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingCommission(null);
                                  setTempValues({});
                                }}
                                className="bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingCommission(advisor.advisor_id);
                                setTempValues({...tempValues, [advisor.advisor_id]: advisor.commission_rate.toString()});
                              }}
                              className="bg-purple-500 text-white p-1 rounded hover:bg-purple-600 transition-colors"
                              title="Edit Commission"
                            >
                              <Settings className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Platform Earnings: </span>
                  <span className="font-bold text-green-600">
                    ${advisorEarnings.reduce((sum, a) => sum + a.total_earned, 0).toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Total Available: </span>
                  <span className="font-bold text-blue-600">
                    ${advisorEarnings.reduce((sum, a) => sum + a.available_balance, 0).toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Total Pending: </span>
                  <span className="font-bold text-yellow-600">
                    ${advisorEarnings.reduce((sum, a) => sum + a.pending_balance, 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Monthly Payouts Tab - New */}
        {activeTab === 'payouts' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Monthly Payout Report</h2>
                  <p className="text-sm text-gray-600 mt-1">Amount to pay advisors for the selected period</p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Period:</label>
                    <select
                      value={payoutDays}
                      onChange={(e) => {
                        const newDays = Number(e.target.value);
                        console.log('Changing payout period from', payoutDays, 'to', newDays, 'days');
                        setPayoutDays(newDays);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={60}>Last 60 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                  </div>
                  <button
                    onClick={fetchMonthlyPayouts}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 bg-gray-50 border-b">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Payouts Due</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-green-600">
                  ${monthlyPayouts.reduce((sum, p) => sum + p.amount_to_pay, 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {monthlyPayouts.length} advisors
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Platform Fees Collected</h3>
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-blue-600">
                  ${monthlyPayouts.reduce((sum, p) => sum + p.platform_fees, 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  From {monthlyPayouts.reduce((sum, p) => sum + p.transaction_count, 0)} transactions
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
                  <Activity className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-purple-600">
                  ${(monthlyPayouts.reduce((sum, p) => sum + p.amount_to_pay, 0) +
                     monthlyPayouts.reduce((sum, p) => sum + p.platform_fees, 0)).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Advisor earnings + Platform fees
                </p>
              </div>
            </div>

            {/* Payout Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Advisor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Earnings</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform Fee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount to Pay</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Paid</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthlyPayouts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                        No transactions found for the selected period
                      </td>
                    </tr>
                  ) : (
                    monthlyPayouts.map((payout) => (
                      <tr key={payout.advisor_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                              <div className="font-medium text-gray-900">{payout.advisor_name}</div>
                              <div className="text-sm text-gray-500">{payout.advisor_email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-gray-600">
                            <Phone className="w-4 h-4 mr-2" />
                            <span className="text-sm">{payout.phone_number || 'Not set'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{payout.transaction_count}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            ${payout.monthly_earnings.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            ${payout.platform_fees.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-lg font-bold text-green-600">
                            ${payout.amount_to_pay.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payout.last_payout_date
                            ? new Date(payout.last_payout_date).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Export/Print Options */}
            <div className="p-6 border-t bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <p className="font-medium">Instructions:</p>
                  <p className="mt-1">1. Review the "Amount to Pay" column - this is what you owe each advisor</p>
                  <p>2. Send payments via your preferred method (bank transfer, PayPal, etc.)</p>
                  <p>3. After paying, update the last_payout_at date in the advisor_wallets table</p>
                </div>
                <button
                  onClick={() => window.print()}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Print Report
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conversations Tab - New */}
        {activeTab === 'conversations' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">All Conversations</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={conversationSearch}
                    onChange={(e) => setConversationSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Conversation List */}
              <div className="lg:col-span-1 border-r max-h-[600px] overflow-y-auto">
                {conversations
                  .filter(conv =>
                    conv.client?.display_name?.toLowerCase().includes(conversationSearch.toLowerCase()) ||
                    conv.advisor?.display_name?.toLowerCase().includes(conversationSearch.toLowerCase())
                  )
                  .map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => {
                        setSelectedConversation(conversation);
                        fetchConversationMessages(conversation.id);
                      }}
                      className={`p-4 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                        selectedConversation?.id === conversation.id ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-sm">
                          {conversation.client?.display_name || 'Unknown Client'}
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          conversation.status === 'active' ? 'bg-green-100 text-green-800' :
                          conversation.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {conversation.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        with {conversation.advisor?.display_name || 'Unknown Advisor'}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(conversation.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
              </div>

              {/* Message View */}
              <div className="lg:col-span-2 max-h-[600px] overflow-y-auto">
                {selectedConversation ? (
                  <div className="p-6">
                    <div className="mb-4 pb-4 border-b">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-lg">
                          Conversation Details
                        </h3>
                        <button
                          onClick={() => handleDeleteConversation(selectedConversation.id)}
                          className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 transition-colors flex items-center text-sm"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete Conversation
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Client:</span>
                          <span className="ml-2 font-medium">{selectedConversation.client?.display_name || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Advisor:</span>
                          <span className="ml-2 font-medium">{selectedConversation.advisor?.display_name || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Status:</span>
                          <span className="ml-2 font-medium">{selectedConversation.status}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Created:</span>
                          <span className="ml-2 font-medium">{new Date(selectedConversation.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {conversationMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${
                            message.sender?.user_type === 'advisor' ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-4 ${
                              message.sender?.user_type === 'advisor'
                                ? 'bg-purple-100'
                                : 'bg-gray-100'
                            }`}
                          >
                            <div className="text-xs font-medium mb-1">
                              {message.sender?.display_name || 'Unknown'}
                              <span className="ml-2 text-gray-500">
                                ({message.sender?.user_type})
                              </span>
                            </div>
                            <div className="text-sm">{message.content}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(message.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>Select a conversation to view messages</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50">
              <p className="text-sm text-gray-600">
                Total Conversations: {conversations.length}
              </p>
            </div>
          </div>
        )}

        {/* Clients Management Tab - New */}
        {activeTab === 'clients' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Client Management</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Free Minutes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message Credits</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients
                    .filter(client =>
                      client.display_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
                      client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                    )
                    .map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-5 h-5 text-gray-400 mr-3" />
                            <span className="font-medium">{client.display_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {client.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-green-600">
                            ${client.wallet_balance.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {addingCreditsTo === `minutes_${client.id}` ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                placeholder="Minutes"
                                value={creditAmount[`minutes_${client.id}`] || ''}
                                onChange={(e) => setCreditAmount({...creditAmount, [`minutes_${client.id}`]: e.target.value})}
                                className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                autoFocus
                              />
                              <button
                                onClick={() => {
                                  const minutes = parseFloat(creditAmount[`minutes_${client.id}`] || '0');
                                  if (!isNaN(minutes) && minutes > 0) {
                                    handleAddCredits(client.id, client.free_minutes + minutes);
                                  }
                                }}
                                className="bg-green-500 text-white p-1 rounded hover:bg-green-600 transition-colors"
                              >
                                <Save className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setAddingCreditsTo(null);
                                  setCreditAmount({});
                                }}
                                className="bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{client.free_minutes.toFixed(1)} min</span>
                              <button
                                onClick={() => setAddingCreditsTo(`minutes_${client.id}`)}
                                className="text-purple-600 hover:text-purple-700"
                                title="Add Minutes"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {addingCreditsTo === `messages_${client.id}` ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="0"
                                placeholder="Credits"
                                value={creditAmount[`messages_${client.id}`] || ''}
                                onChange={(e) => setCreditAmount({...creditAmount, [`messages_${client.id}`]: e.target.value})}
                                className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                autoFocus
                              />
                              <button
                                onClick={() => {
                                  const credits = parseInt(creditAmount[`messages_${client.id}`] || '0');
                                  if (!isNaN(credits) && credits > 0) {
                                    handleAddMessageCredits(client.id, client.message_credits + credits);
                                  }
                                }}
                                className="bg-green-500 text-white p-1 rounded hover:bg-green-600 transition-colors"
                              >
                                <Save className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setAddingCreditsTo(null);
                                  setCreditAmount({});
                                }}
                                className="bg-gray-500 text-white p-1 rounded hover:bg-gray-600 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{client.message_credits}</span>
                              <button
                                onClick={() => setAddingCreditsTo(`messages_${client.id}`)}
                                className="text-purple-600 hover:text-purple-700"
                                title="Add Message Credits"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-sm">
                          {new Date(client.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setEditingWallet(client.id);
                              const wallet = clientWallets.find(w => w.user_id === client.id);
                              if (wallet) {
                                setTempValues({...tempValues, [wallet.id]: wallet.balance.toString()});
                                setActiveTab('wallets');
                              }
                            }}
                            className="text-purple-600 hover:text-purple-700 flex items-center"
                          >
                            <Wallet className="w-4 h-4 mr-1" />
                            Edit Wallet
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Clients: </span>
                  <span className="font-bold">{clients.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Wallet Balance: </span>
                  <span className="font-bold text-green-600">
                    ${clients.reduce((sum, c) => sum + c.wallet_balance, 0).toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Total Free Minutes: </span>
                  <span className="font-bold text-blue-600">
                    {clients.reduce((sum, c) => sum + c.free_minutes, 0).toFixed(1)} min
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Total Message Credits: </span>
                  <span className="font-bold text-purple-600">
                    {clients.reduce((sum, c) => sum + c.message_credits, 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Voice Calls Tab - New */}
        {activeTab === 'voicecalls' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Voice Call History</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search calls..."
                    value={voiceCallSearch}
                    onChange={(e) => setVoiceCallSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6 bg-gray-50 border-b">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Calls</h3>
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-2xl font-bold">{voiceCalls.length}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Completed Calls</h3>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-2xl font-bold">
                  {voiceCalls.filter(c => c.status === 'completed').length}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Duration</h3>
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-2xl font-bold">
                  {(voiceCalls.reduce((sum, c) => sum + (c.duration || 0), 0) / 60).toFixed(1)} min
                </p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-2xl font-bold">
                  ${voiceCalls.reduce((sum, c) => sum + (c.cost || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Calls Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Advisor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {voiceCalls
                    .filter(call =>
                      call.client?.display_name?.toLowerCase().includes(voiceCallSearch.toLowerCase()) ||
                      call.advisor?.display_name?.toLowerCase().includes(voiceCallSearch.toLowerCase()) ||
                      call.call_sid?.toLowerCase().includes(voiceCallSearch.toLowerCase())
                    )
                    .map((call) => (
                      <tr key={call.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                              <div className="font-medium">{call.client?.display_name || 'Unknown'}</div>
                              <div className="text-sm text-gray-500">{call.client?.email || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-5 h-5 text-gray-400 mr-3" />
                            <div>
                              <div className="font-medium">{call.advisor?.display_name || 'Unknown'}</div>
                              <div className="text-sm text-gray-500">{call.advisor?.email || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(call.start_time).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium">
                            {call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            call.status === 'completed' ? 'bg-green-100 text-green-800' :
                            call.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                            call.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {call.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-green-600">
                            ${(call.cost || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {call.rating ? (
                            <div className="flex items-center">
                              <span className="text-yellow-500 mr-1">★</span>
                              <span className="font-medium">{call.rating}/5</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {voiceCalls.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No voice calls recorded yet</p>
              </div>
            )}

            <div className="p-6 border-t bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Calls: </span>
                  <span className="font-bold">{voiceCalls.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Avg Duration: </span>
                  <span className="font-bold">
                    {voiceCalls.length > 0
                      ? `${(voiceCalls.reduce((sum, c) => sum + (c.duration || 0), 0) / voiceCalls.length / 60).toFixed(1)} min`
                      : '0 min'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Avg Cost: </span>
                  <span className="font-bold text-green-600">
                    ${voiceCalls.length > 0
                      ? (voiceCalls.reduce((sum, c) => sum + (c.cost || 0), 0) / voiceCalls.length).toFixed(2)
                      : '0.00'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Credits Manager Tab */}
        {activeTab === 'credits' && (
          <CreditsManager />
        )}

        {/* Blog Manager Tab - Existing */}
        {activeTab === 'blog' && (
          <BlogManager />
        )}
      </div>
    </div>
  );
}